'use client';

import { type MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';

import { dispatchAudioPlay, generateAudioInstanceId } from './AudioPlayEvent';
import { useAudioCoordination } from './useAudioCoordination';

export interface UsePlayAudioPayload {
    isPlaying: boolean;
    handlePlayPause: MouseEventHandler<HTMLButtonElement>;
    audioRef: React.RefObject<HTMLAudioElement | null>;
}

export const usePlayAudio = (
    audioUrl: string,
    voiceId: string,
    playingVoiceId: string | null,
    onPlayHandler?: (voiceId: string) => void,
    onEndedHandler?: (voiceId: string) => void,
): UsePlayAudioPayload => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    // Generate a stable unique instance ID for this hook instance
    const instanceIdRef = useRef(generateAudioInstanceId());

    // Store callbacks in refs to avoid effect churn when callers pass inline functions
    const onPlayHandlerRef = useRef(onPlayHandler);
    const onEndedHandlerRef = useRef(onEndedHandler);

    // Sync callback refs in effects — safe because they are only read inside event handlers
    useEffect(() => {
        onPlayHandlerRef.current = onPlayHandler;
    }, [onPlayHandler]);

    useEffect(() => {
        onEndedHandlerRef.current = onEndedHandler;
    }, [onEndedHandler]);

    const handlePlayPause: MouseEventHandler<HTMLButtonElement> = useCallback(
        (event) => {
            event.stopPropagation();

            if (!audioUrl) {
                return;
            }

            const audio = audioRef.current;
            if (audio) {
                if (isPlaying) {
                    audio.pause();
                    setIsPlaying(false);
                } else {
                    audio.currentTime = 0;
                    void audio
                        .play()
                        .then(() => {
                            setIsPlaying(true);
                            // Dispatch global event to pause other audio sources
                            // Only dispatch after successful play to avoid unnecessary pauses
                            // Use instanceId to identify THIS player specifically
                            dispatchAudioPlay(audioUrl, instanceIdRef.current);
                        })
                        .catch(() => {
                            setIsPlaying(false);
                        });
                }
            }
        },
        [isPlaying, audioUrl],
    );

    // Initialize audio element and set up event listeners
    // Only re-run when audioUrl or voiceId changes, NOT when callbacks change
    useEffect(() => {
        if (!audioUrl) {
            audioRef.current = null;
            return;
        }

        audioRef.current = new Audio(audioUrl);

        const audio = audioRef.current;

        const handlePlay = (): void => {
            onPlayHandlerRef.current?.(voiceId);
        };
        const handleEnded = (): void => {
            onEndedHandlerRef.current?.(voiceId);
            setIsPlaying(false);
        };
        const handleError = (): void => {
            setIsPlaying(false);
        };
        const handleAudioPause = (): void => {
            setIsPlaying(false);
        };
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);
        audio.addEventListener('pause', handleAudioPause);

        return () => {
            audio.pause();
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('pause', handleAudioPause);
            audio.src = '';
        };
    }, [audioUrl, voiceId]);

    // Pause when another voice card starts playing (via playingVoiceId state)
    useEffect(() => {
        // Only pause if playingVoiceId is set and it's a different voice
        if (playingVoiceId !== null && playingVoiceId !== voiceId && audioRef.current) {
            audioRef.current.pause();
        }
    }, [playingVoiceId, voiceId]);

    const handlePause = useCallback((): void => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        setIsPlaying(false);
    }, []);

    // Pass the ref object (not .current) so useAudioCoordination reads it inside effects
    useAudioCoordination({
        instanceIdRef,
        onPause: handlePause,
    });

    return {
        isPlaying,
        handlePlayPause,
        audioRef,
    };
};
